import { FC } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { Logo } from '~/components/logo';
import { SectionCard } from '~/components/section-card';
import { SectionGroup } from '~/components/section-group';
import { supabase } from '~/libs/supabase';

export const resetPasswordSchema = z.object({
  email: z.string().email(),
});

export type ResetPassword = z.infer<typeof resetPasswordSchema>;

export const ResetPasswordForm: FC = () => {
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    shouldUseNativeValidation: false,
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: {
      email: '',
    } as ResetPassword,
  });

  const onSubmit = async (values: ResetPassword) => {
    const { error } = await supabase.auth.api.resetPasswordForEmail(values.email);

    if (error) {
      toast({
        title: 'An error occurred',
        description: error.message ? error.message : 'An error occurred, please try again',
        status: 'error',
        isClosable: true,
      });

      console.error(error);
    } else {
      toast({
        title: 'Password reset started',
        description: 'Check your email for the next step',
        status: 'info',
        isClosable: true,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SectionCard title='Reset password' footerSlot={<SectionFooter isValid={isValid} isSubmitting={isSubmitting} />}>
        <Stack spacing={8}>
          <SectionGroup>
            <Stack spacing={4}>
              <FormControl isInvalid={Boolean(errors.email)}>
                <FormLabel htmlFor='email'>Email address</FormLabel>
                <Box maxW='sm'>
                  <Input
                    id='email'
                    type='email'
                    {...register('email', {
                      required: 'Email is required',
                    })}
                  />
                </Box>
                <FormErrorMessage>
                  <>{errors.email?.message}</>
                </FormErrorMessage>
              </FormControl>
            </Stack>
          </SectionGroup>
        </Stack>
      </SectionCard>
    </form>
  );
};

const SectionFooter: FC<{ isValid: boolean; isSubmitting: boolean }> = ({ isValid, isSubmitting }) => {
  return (
    <Flex justifyContent='space-between' gap={4} flexDir={{ base: 'column-reverse', lg: 'row' }}>
      <Logo />
      <Flex alignItems='center' gap={4} flexDir={{ base: 'column-reverse', lg: 'row' }}>
        <Text fontSize='xs'>
          <Link href='/sign-up'>
            <a>
              <Text as='span' fontSize='xs' fontWeight='bold' textDecor='underline'>
                Go back to log in
              </Text>
            </a>
          </Link>
        </Text>
        <Button isLoading={isSubmitting} disabled={!isValid || isSubmitting} type='submit'>
          Reset password
        </Button>
      </Flex>
    </Flex>
  );
};
